/**
 * Pure helpers for the evidence workspace (no React Native imports so vitest
 * can run them in the node environment).
 */
import { getLevelForm, type IcfFormLevel, type IcfFormStatement } from '../../../data/icfFormStatements';
import type {
  EvidenceCitation,
  EvidencePolarity,
  SessionObservationForm,
  SessionTranscript,
  SofAiRating,
  SofEvidenceFeedback,
  SofMentorRating,
  TranscriptTurn,
} from '../../../types/mentorEngagements';
import { deriveReviewState, type ReviewState, type SofReviewState } from '../sofReview';

export interface EvidenceMoment extends EvidenceCitation {
  polarity: EvidencePolarity;
  /** Stable when the server hashed it; index-based fallback is display-only. */
  key: string;
  hasKey: boolean;
  sortIndex: number;
}

export type RelevanceState = 'unreviewed' | 'relevant' | 'not_relevant';

export interface RelevanceLookup {
  verdictFor: (citationKey: string) => SofEvidenceFeedback | undefined;
  isRejected: (moment: EvidenceMoment) => boolean;
}

export const LOW_CONFIDENCE = 0.6;
export const TOP_MOMENTS = 3;

export const REVIEW_RAIL_STATES = ['unrated', 'ai_suggested', 'confirmed', 'overridden', 'evidence_rejected'] as const;
export type RailState = (typeof REVIEW_RAIL_STATES)[number];

const parseTimestamp = (value?: string | null): number | null => {
  if (!value) return null;
  const parts = value.trim().split(':').map(Number);
  if (parts.some(Number.isNaN) || !parts.length) return null;
  return parts.reduce((total, part) => total * 60 + part, 0);
};

/** Merge supports + counter lists into one timeline ordered by time, then turn, then original order. */
export function buildTimeline(ai: SofAiRating | undefined): EvidenceMoment[] {
  const moments: EvidenceMoment[] = [];
  const push = (items: EvidenceCitation[] | undefined, fallback: EvidencePolarity) => {
    (items ?? []).forEach((citation, index) => {
      const polarity = citation.polarity ?? fallback;
      const hasKey = typeof citation.citation_key === 'string' && citation.citation_key.length > 0;
      const key = hasKey ? citation.citation_key! : `${polarity}:${index}`;
      if (moments.some(moment => moment.key === key)) return;
      moments.push({ ...citation, polarity, key, hasKey, sortIndex: moments.length });
    });
  };
  push(ai?.evidence, 'supports');
  push(ai?.counter_evidence, 'contradicts');
  return moments.sort((a, b) => {
    const ta = parseTimestamp(a.timestamp); const tb = parseTimestamp(b.timestamp);
    if (ta !== null && tb !== null && ta !== tb) return ta - tb;
    if ((ta === null) !== (tb === null)) return ta === null ? 1 : -1;
    const ia = a.turn_index ?? null; const ib = b.turn_index ?? null;
    if (ia !== null && ib !== null && ia !== ib) return ia - ib;
    if ((ia === null) !== (ib === null)) return ia === null ? 1 : -1;
    return a.sortIndex - b.sortIndex;
  });
}

export const hasTimeReference = (moment: EvidenceMoment) => parseTimestamp(moment.timestamp) !== null || moment.turn_index != null;

/** Top = first N supports plus every contradicts card; expanded shows all. */
export function visibleMoments(moments: EvidenceMoment[], expanded: boolean): { shown: EvidenceMoment[]; hidden: number } {
  if (expanded) return { shown: moments, hidden: 0 };
  let supports = 0;
  const shown = moments.filter(moment => {
    if (moment.polarity === 'contradicts') return true;
    supports += 1;
    return supports <= TOP_MOMENTS;
  });
  return { shown, hidden: moments.length - shown.length };
}

export function buildRelevanceLookup(
  stored: SofEvidenceFeedback[] | undefined,
  local: Record<string, SofEvidenceFeedback> | undefined,
  mentor: SofMentorRating | null | undefined,
): RelevanceLookup {
  const merged = new Map<string, SofEvidenceFeedback>();
  (stored ?? []).forEach(item => merged.set(item.citation_key, item));
  Object.values(local ?? {}).forEach(item => merged.set(item.citation_key, item));
  const rejectedKeys = new Set(mentor?.rejected_citation_keys ?? []);
  return {
    verdictFor: key => merged.get(key),
    // An explicit mark (stored or local) wins over the server-derived list so "reopen" can undo a rejection.
    isRejected: moment => {
      if (!moment.hasKey) return false;
      const mark = merged.get(moment.key);
      return mark ? mark.verdict === 'not_relevant' : rejectedKeys.has(moment.key);
    },
  };
}

export function relevanceState(moment: EvidenceMoment, lookup: RelevanceLookup): RelevanceState {
  if (!moment.hasKey) return 'unreviewed';
  if (lookup.isRejected(moment)) return 'not_relevant';
  return lookup.verdictFor(moment.key)?.verdict === 'relevant' ? 'relevant' : 'unreviewed';
}

/** Every citation of the statement rejected (binding contract). Never true without citations. */
export function allSupportRejected(moments: EvidenceMoment[], lookup: RelevanceLookup): boolean {
  return moments.length > 0 && moments.every(moment => lookup.isRejected(moment));
}

export function countKept(moments: EvidenceMoment[], lookup: RelevanceLookup): number {
  return moments.filter(moment => !lookup.isRejected(moment)).length;
}

export interface ReviewItem {
  id: string;
  competency: string;
  statement: IcfFormStatement;
  reviewState: ReviewState;
  railState: RailState;
  lowConfidence: boolean;
}

export function railStateFor(reviewState: ReviewState, allRejected: boolean): RailState {
  return allRejected && reviewState !== 'overridden' ? 'evidence_rejected' : reviewState;
}

/** Flatten the catalog into ordered review items with derived states. */
export function buildReviewItems(
  level: IcfFormLevel,
  form: Pick<SessionObservationForm, 'ai_ratings' | 'mentor_ratings' | 'evidence_feedback'>,
  local: Pick<SofReviewState, 'mentorRatings' | 'evidenceFeedback'>,
): ReviewItem[] {
  const items: ReviewItem[] = [];
  for (const competency of getLevelForm(level).competencies) {
    for (const statement of competency.statements) {
      const id = statement.id;
      const ai = form.ai_ratings[id];
      const touched = Object.prototype.hasOwnProperty.call(local.mentorRatings, id);
      const mentor = touched ? local.mentorRatings[id] ?? undefined : form.mentor_ratings[id];
      const { state } = deriveReviewState(ai, mentor, level);
      const lookup = buildRelevanceLookup(form.evidence_feedback, local.evidenceFeedback, mentor);
      const allRejected = allSupportRejected(buildTimeline(ai), lookup);
      items.push({
        id, competency: competency.code, statement, reviewState: state,
        railState: railStateFor(state, allRejected),
        lowConfidence: ai?.confidence != null && ai.confidence < LOW_CONFIDENCE,
      });
    }
  }
  return items;
}

export const isDone = (state: ReviewState) => state === 'confirmed' || state === 'overridden';

export function nextUnfinished(items: ReviewItem[], fromId: string | null): string | null {
  const start = fromId ? items.findIndex(item => item.id === fromId) : -1;
  for (let offset = 1; offset <= items.length; offset++) {
    const item = items[(start + offset) % items.length];
    if (!isDone(item.reviewState)) return item.id;
  }
  return null;
}

/** Window of turns around a cited turn; `undefined` transcript means unavailable. */
export function transcriptWindow(transcript: SessionTranscript | undefined, center: number, before: number, after: number): TranscriptTurn[] {
  if (!transcript) return [];
  return transcript.turns.filter(turn => turn.turn_index >= center - before && turn.turn_index <= center + after);
}
